<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import {
  CAMPAIGN_DYNAMIC_VARIABLES,
  VARIABLE_CHIP_CLASS,
  VARIABLE_CHIP_HIGHLIGHTED_CLASS,
} from 'dashboard/components-next/Campaigns/shared/campaignVariables';

defineProps({
  wizardData: { type: Object, required: true },
});

const emit = defineEmits(['update']);
const { t } = useI18n();

const templateParserRef = ref(null);
const showErrors = ref(false);

const isValid = computed(
  () => templateParserRef.value?.isFormInvalid === false
);

const validate = () => {
  showErrors.value = true;
  return isValid.value;
};

defineExpose({ isValid, validate });

const copyToClipboard = async text => {
  try {
    await navigator.clipboard.writeText(text);
    useAlert(t('CONTACT_PANEL.COPY_SUCCESSFUL'));
  } catch {
    /* clipboard not available */
  }
};

watch(
  () => templateParserRef.value?.processedParams,
  params => {
    if (params) {
      emit('update', {
        processedParams: params,
        renderedTemplate: templateParserRef.value?.renderedTemplate || '',
      });
    }
  },
  { deep: true }
);
</script>

<template>
  <div class="flex flex-col gap-4">
    <h3 class="text-base font-medium text-n-slate-12">
      {{ t('CAMPAIGN.WHATSAPP.WIZARD.VARIABLE_CONFIG.TITLE') }}
    </h3>

    <!-- Dynamic Variables Hint -->
    <div class="p-3 text-xs rounded-lg bg-n-alpha-2 border border-n-weak">
      <p class="font-medium text-n-slate-12 mb-1.5">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.DYNAMIC_VARIABLES.TITLE') }}
      </p>
      <p class="text-n-slate-11 mb-2">
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.VARIABLE_CONFIG.DESCRIPTION') }}
      </p>
      <div class="flex flex-wrap gap-1.5">
        <code
          v-for="variable in CAMPAIGN_DYNAMIC_VARIABLES"
          :key="variable.key"
          class="px-1.5 py-0.5 rounded font-mono text-[11px] cursor-pointer hover:brightness-110 transition-all"
          :class="
            variable.highlighted
              ? VARIABLE_CHIP_HIGHLIGHTED_CLASS
              : VARIABLE_CHIP_CLASS
          "
          @click="copyToClipboard(variable.key)"
        >
          {{ variable.key }}
        </code>
      </div>
    </div>

    <!-- Template Parser with variable inputs -->
    <WhatsAppTemplateParser
      v-if="wizardData.selectedTemplate"
      ref="templateParserRef"
      allow-liquid
      :template="wizardData.selectedTemplate"
      :show-errors="showErrors"
    />
  </div>
</template>
